import type { CollectionNode, NumistaTarget } from '@numistic/contracts'
import {
  DataError,
  useCreateSpecimen,
  useNumistaImport,
  useNumistaStatus,
  useTree,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button, Checkbox, Select } from '@numistic/ui'
import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { useErrorText } from '../lib/errors.js'

/** Выбор коллекции деревом с отступами; пустое значение — «Входящие». */
export function CollectionPicker({
  value,
  onChange,
  label,
}: {
  value: string
  onChange: (id: string) => void
  label: string
}) {
  const { t } = useTranslation()
  const data = useTree('collections').data
  const tree = useMemo(() => (data ?? []) as CollectionNode[], [data])
  const rows = useMemo(() => {
    const children = new Map<string | null, CollectionNode[]>()
    for (const n of tree) children.set(n.parentId, [...(children.get(n.parentId) ?? []), n])
    const out: Array<{ id: string; name: string; depth: number }> = []
    const walk = (parent: string | null, depth: number) => {
      for (const n of children.get(parent) ?? []) {
        out.push({ id: n.id, name: n.name, depth })
        walk(n.id, depth + 1)
      }
    }
    walk(null, 0)
    return out
  }, [tree])
  return (
    <Select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-auto min-w-44"
    >
      <option value="">{t('numista.bulk.inbox')}</option>
      {rows
        .filter((r) => !tree.find((n) => n.id === r.id)?.isInbox)
        .map((r) => (
          <option key={r.id} value={r.id}>
            {`${'  '.repeat(r.depth)}${r.name}`}
          </option>
        ))}
    </Select>
  )
}

export type ImportOutcome =
  | { kind: 'done'; itemId: string; specimenId?: string }
  | { kind: 'issuer' }
  | { kind: 'error'; message: string }

/**
 * Импорт одного типа Numista и, если нужно, сразу экземпляр в коллекции. Не уводит со страницы:
 * результат — ссылки, которые открываются в новой вкладке.
 */
export function useImportToCollection() {
  const run = useNumistaImport()
  const addSpecimen = useCreateSpecimen()
  const errorText = useErrorText()
  return async (input: {
    numistaId: number
    target: NumistaTarget
    typeCode?: string
    issuerId?: string
    translate: boolean
    /** undefined — не добавлять; '' — во «Входящие»; иначе id коллекции. */
    collectionId?: string
  }): Promise<ImportOutcome> => {
    const { collectionId, ...importInput } = input
    try {
      const item = await run.mutateAsync(importInput)
      if (collectionId === undefined) return { kind: 'done', itemId: item.id }
      const specimen = await addSpecimen.mutateAsync({
        catalogItemId: item.id,
        values: collectionId ? { collection: collectionId } : {},
      })
      return { kind: 'done', itemId: item.id, specimenId: specimen.id }
    } catch (error) {
      if (
        error instanceof DataError &&
        error.code === 'VALIDATION_FAILED' &&
        error.issues.some((i) => i.path[0] === 'issuer')
      )
        return { kind: 'issuer' }
      return { kind: 'error', message: errorText.request(error) ?? String(error) }
    }
  }
}

/** Ссылки на результат импорта — в новой вкладке, поиск остаётся на месте. */
export function OutcomeLinks({ outcome }: { outcome: Extract<ImportOutcome, { kind: 'done' }> }) {
  const { t } = useTranslation()
  const link = 'text-primary underline underline-offset-4 hover:decoration-2'
  return (
    <span className="inline-flex flex-wrap gap-x-3">
      <Link to="/catalog/$id" params={{ id: outcome.itemId }} target="_blank" className={link}>
        {t('numista.bulk.openItem')} ↗
      </Link>
      {outcome.specimenId ? (
        <Link to="/s/$id" params={{ id: outcome.specimenId }} target="_blank" className={link}>
          {t('numista.bulk.openSpecimen')} ↗
        </Link>
      ) : null}
    </span>
  )
}

type Row = { numistaId: number; title: string; state: 'wait' | 'run' | ImportOutcome }

/**
 * Несколько типов из поиска за раз: импорт по очереди (в каталог или в мои записи) и, по
 * желанию, экземпляр каждого — в выбранную коллекцию. Не сопоставленный эмитент — пропуск
 * со ссылкой на предпросмотр, где его можно выбрать вручную.
 */
export function BulkImportBar({
  selected,
  editor,
  onClear,
  onDone,
  onOpen,
}: {
  selected: Map<number, string>
  editor: boolean
  onClear: () => void
  /** Успешно импортированные — снять с выделения. */
  onDone: (numistaId: number) => void
  onOpen: (numistaId: number) => void
}) {
  const { t } = useTranslation()
  const status = useNumistaStatus().data
  const importOne = useImportToCollection()
  const [target, setTarget] = useState<NumistaTarget>(editor ? 'catalog' : 'private')
  const [toCollection, setToCollection] = useState(true)
  const [collectionId, setCollectionId] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const running = rows.some((r) => r.state === 'wait' || r.state === 'run')
  const count = selected.size
  // Тип, который ещё не открывали, — до двух запросов (тип и выпуски); открытые — из кэша.
  const left = status ? status.quota - status.used : undefined
  const short = left !== undefined && left < count * 2

  const start = async () => {
    const queue: Row[] = [...selected].map(([numistaId, title]) => ({
      numistaId,
      title,
      state: 'wait',
    }))
    setRows(queue)
    for (const row of queue) {
      setRows((rs) => rs.map((r) => (r.numistaId === row.numistaId ? { ...r, state: 'run' } : r)))
      const outcome = await importOne({
        numistaId: row.numistaId,
        target,
        translate: true,
        collectionId: toCollection ? collectionId : undefined,
      })
      setRows((rs) => rs.map((r) => (r.numistaId === row.numistaId ? { ...r, state: outcome } : r)))
      if (outcome.kind === 'done') onDone(row.numistaId)
    }
  }

  if (!count && !rows.length) return null
  return (
    <section
      aria-label={t('numista.bulk.title')}
      className="grid gap-3 rounded-lg border border-primary/40 bg-card p-3 shadow-sm"
    >
      {count ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="mr-auto font-medium">{t('numista.bulk.selected', { count })}</span>
            <Button type="button" variant="ghost" size="sm" disabled={running} onClick={onClear}>
              {t('numista.bulk.clear')}
            </Button>
          </div>
          {editor ? (
            <Select
              aria-label={t('numista.bulk.target')}
              value={target}
              disabled={running}
              onChange={(e) => setTarget(e.target.value as NumistaTarget)}
            >
              <option value="catalog">{t('numista.importCatalog')}</option>
              <option value="private">{t('numista.importPrivate')}</option>
            </Select>
          ) : null}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={toCollection}
              disabled={running}
              onChange={(e) => setToCollection(e.target.checked)}
            />
            {t('numista.bulk.addToCollection')}
          </label>
          {toCollection ? (
            <CollectionPicker
              label={t('numista.bulk.collection')}
              value={collectionId}
              onChange={setCollectionId}
            />
          ) : null}
          <p className={short ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
            {t('numista.bulk.quotaHint', { requests: count * 2 })}
            {left !== undefined ? ` ${t('numista.bulk.quotaLeft', { left })}` : ''}
          </p>
          <Button type="button" disabled={running} onClick={() => void start()}>
            {running
              ? t('numista.bulk.running')
              : editor && target === 'catalog'
                ? t('numista.bulk.runCatalog', { count })
                : t('numista.bulk.runPrivate', { count })}
          </Button>
        </>
      ) : null}
      {rows.length ? (
        <ol className="grid gap-1.5 text-sm" aria-live="polite">
          {rows.map((r) => (
            <li key={r.numistaId} className="grid gap-0.5">
              <span className="flex items-start gap-2">
                <span aria-hidden className="w-4 shrink-0 text-center">
                  {r.state === 'wait'
                    ? '·'
                    : r.state === 'run'
                      ? '…'
                      : r.state.kind === 'done'
                        ? '✓'
                        : '⚠'}
                </span>
                <span className="min-w-0 flex-1 truncate">{r.title}</span>
              </span>
              {typeof r.state === 'object' ? (
                <span className="pl-6 text-xs text-muted-foreground">
                  {r.state.kind === 'done' ? (
                    <OutcomeLinks outcome={r.state} />
                  ) : r.state.kind === 'issuer' ? (
                    <>
                      {t('numista.bulk.issuerSkipped')}{' '}
                      <button
                        type="button"
                        className="text-primary underline underline-offset-4"
                        onClick={() => onOpen(r.numistaId)}
                      >
                        {t('numista.bulk.pickIssuer')}
                      </button>
                    </>
                  ) : (
                    <span className="text-destructive">{r.state.message}</span>
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {rows.length && !running ? (
        <Button type="button" variant="ghost" size="sm" onClick={() => setRows([])}>
          {t('numista.bulk.hideReport')}
        </Button>
      ) : null}
    </section>
  )
}
